/**
 * 生命火种技能树（15 节点）
 *
 * 设计原则（GDD 3.2）：**技能效果本身就是知识的应用**。
 * 每个节点绑定一张真实知识卡（`cardId`），首次解锁时弹出「知识图鉴」讲解——
 * 玩家不是为了升级而学，而是"学会了一个知识点，于是火种更强了"。
 *
 * 全树累计消耗 189 智慧值，因此**单关只开放子集**（见 `levels/chapter1.ts`）；
 * 第一章的"15 节点"是整章累计可解锁的规模。
 *
 * 节点效果语义：
 * - spreadSpeed 百分比加成（+10 = 单次传播量 ×1.10，多个节点乘算）
 * - spreadRadius 传播波及：额外把启蒙度溢出到相邻区域（每点 +4）
 * - energyRegen / maxEnergy 数值加成（加法）
 * - mistResist 每回合抵扣的迷雾增长
 * - revealRoute 额外显现的隐藏海路条数（按地图声明顺序）
 */

import type { SkillNode } from "../types.js";

export const LIFE_SKILL_TREE: readonly SkillNode[] = [
  {
    id: "life-root",
    torch: "life",
    name: "认识三个环节",
    cardId: "biology-001",
    cost: 5,
    effects: [{ kind: "energyRegen", value: 5 }],
  },
  {
    id: "life-hygiene",
    torch: "life",
    name: "水与洁净",
    cardId: "biology-002",
    parentId: "life-root",
    cost: 6,
    effects: [{ kind: "mistResist", value: 2 }],
  },
  {
    id: "life-observe",
    torch: "life",
    name: "观察与记录",
    cardId: "biology-012",
    parentId: "life-root",
    cost: 7,
    effects: [{ kind: "spreadRadius", value: 1 }],
  },
  {
    id: "life-nutrition",
    torch: "life",
    name: "营养与体质",
    cardId: "biology-005",
    parentId: "life-hygiene",
    cost: 9,
    effects: [{ kind: "maxEnergy", value: 10 }],
  },
  {
    id: "life-quarantine",
    torch: "life",
    name: "隔离与检疫",
    cardId: "biology-003",
    parentId: "life-hygiene",
    cost: 10,
    effects: [{ kind: "mistResist", value: 3 }],
  },
  {
    id: "life-vectors",
    torch: "life",
    name: "病媒生物",
    cardId: "biology-010",
    parentId: "life-root",
    cost: 10,
    effects: [{ kind: "spreadSpeed", value: 10 }],
  },
  {
    id: "life-vaccine",
    torch: "life",
    name: "疫苗的火种",
    cardId: "biology-007",
    parentId: "life-root",
    cost: 12,
    effects: [{ kind: "spreadSpeed", value: 15 }],
  },
  {
    id: "life-epimap",
    torch: "life",
    name: "疫病地图",
    cardId: "biology-013",
    parentId: "life-observe",
    cost: 12,
    effects: [{ kind: "revealRoute", value: 1 }],
  },
  {
    id: "life-stats",
    torch: "life",
    name: "数据与统计",
    cardId: "math-001",
    parentId: "life-observe",
    cost: 13,
    effects: [{ kind: "energyRegen", value: 8 }],
  },
  {
    id: "life-disinfect",
    torch: "life",
    name: "消毒与灭菌",
    cardId: "biology-004",
    parentId: "life-quarantine",
    cost: 14,
    effects: [
      { kind: "spreadSpeed", value: 10 },
      { kind: "mistResist", value: 2 },
    ],
  },
  {
    id: "life-ecology",
    torch: "life",
    name: "生态平衡",
    cardId: "biology-011",
    parentId: "life-vectors",
    cost: 14,
    effects: [{ kind: "maxEnergy", value: 15 }],
  },
  {
    id: "life-immunity",
    torch: "life",
    name: "免疫与抗体",
    cardId: "biology-006",
    parentId: "life-nutrition",
    cost: 15,
    effects: [{ kind: "mistResist", value: 4 }],
  },
  {
    id: "life-herd",
    torch: "life",
    name: "群体免疫",
    cardId: "biology-008",
    parentId: "life-vaccine",
    cost: 18,
    effects: [{ kind: "mistResist", value: 5 }],
  },
  {
    id: "life-eradicate",
    torch: "life",
    name: "消灭天花",
    cardId: "biology-009",
    parentId: "life-herd",
    cost: 20,
    effects: [
      { kind: "spreadSpeed", value: 25 },
      { kind: "mistResist", value: 3 },
    ],
  },
  {
    id: "life-torchlight",
    torch: "life",
    name: "举灯传火",
    cardId: "biology-014",
    parentId: "life-eradicate",
    cost: 24,
    effects: [
      { kind: "spreadRadius", value: 2 },
      { kind: "spreadSpeed", value: 20 },
    ],
  },
];

/** 每点 spreadRadius 把多少启蒙度溢出到相邻区域 */
export const SPREAD_RADIUS_UNIT = 4;

const LIFE_BY_ID = new Map(LIFE_SKILL_TREE.map((n) => [n.id, n]));

export function getLifeSkillNode(id: string): SkillNode | undefined {
  return LIFE_BY_ID.get(id);
}
